/**
 * OrcamentosPage Component
 * 
 * Página de listagem e gerenciamento de orçamentos.
 * Permite visualizar, criar, editar e gerenciar orçamentos.
 * 
 * Funcionalidades:
 * - Listagem de orçamentos com paginação
 * - Busca por número ou cliente
 * - Ações por linha (visualizar, editar, aprovar, cancelar, etc)
 * - Link para criar novo orçamento
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Edit, Trash2, Plus, Search, FileText, CheckCircle, XCircle, 
  Clock, Download, Package, Eye, AlertCircle, MoreVertical,
  Cpu, RotateCcw, Copy
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import DataTable from '../../../components/ERP/DataTable/DataTable'
import { quoteService, customerService } from '../../../services/erpApi'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import QuotePDFTemplate from '../../../components/ERP/Orcamentos/QuotePDFTemplate'
import { Toast } from '../../../components'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import './OrcamentosPage.css'

function OrcamentosPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  const [orcamentos, setOrcamentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  const [filters, setFilters] = useState({
    search: ''
  })
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })
  const [activeMenu, setActiveMenu] = useState(null)
  const menuRef = useRef(null)
  const pdfItemsRef = useRef(null)
  const pdfSummaryRef = useRef(null)
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [pdfQuoteData, setPdfQuoteData] = useState(null)
  const [engineLogs, setEngineLogs] = useState('Iniciando Motor...')

  const loadOrcamentos = useCallback(async () => {
    setLoading(true)
    try {
      const response = await quoteService.getAll({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      })
      
      if (response.success) {
        // Filtrar itens inválidos (null, undefined) e garantir que todos tenham status
        const validOrcamentos = (response.data || []).filter(item => 
          item && item !== null && typeof item === 'object'
        )
        setOrcamentos(validOrcamentos)
        if (response.pagination) {
          setPagination(response.pagination)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar orçamentos:', error)
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.limit, pagination.page])

  useEffect(() => {
    loadOrcamentos()
  }, [loadOrcamentos])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 1 })) // Resetar para primeira página
  }

  const handleRevertToDraft = async (orcamento) => {
    setConfirmModal({
      isOpen: true,
      title: 'Voltar para Rascunho',
      message: `Deseja reverter o orçamento #${orcamento.number} para rascunho? Ele poderá ser editado novamente.`,
      onConfirm: async () => {
        try {
          const response = await quoteService.revertToDraft(orcamento.id)
          if (response.success) {
            showToast('Orçamento voltou para rascunho!', 'success')
            loadOrcamentos()
          } else {
            showToast(response.message || 'Erro ao reverter orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao reverter orçamento:', error)
          showToast(error.response?.data?.message || 'Erro ao reverter orçamento', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  const handleApprove = async (orcamento) => {
    setConfirmModal({
      isOpen: true,
      title: 'Aprovar Orçamento',
      message: `Deseja aprovar o orçamento #${orcamento.number}?`,
      onConfirm: async () => {
        try {
          const response = await quoteService.approve(orcamento.id)
          if (response.success) {
            showToast('Orçamento aprovado com sucesso!', 'success')
            loadOrcamentos()
          } else {
            showToast(response.message || 'Erro ao aprovar orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao aprovar orçamento:', error)
          showToast(error.response?.data?.message || 'Erro ao aprovar orçamento', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  const handleCancel = async (orcamento) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancelar Orçamento',
      message: `Deseja cancelar o orçamento #${orcamento.number}?`,
      onConfirm: async () => {
        try {
          const response = await quoteService.cancel(orcamento.id)
          if (response.success) {
            showToast('Orçamento cancelado com sucesso!', 'success')
            loadOrcamentos()
          } else {
            showToast(response.message || 'Erro ao cancelar orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao cancelar orçamento:', error)
          showToast(error.response?.data?.message || 'Erro ao cancelar orçamento', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  const handleDelete = async (orcamento) => {
    setConfirmModal({
      isOpen: true,
      title: 'Deletar Orçamento',
      message: `Deseja realmente deletar o orçamento #${orcamento.number}? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        try {
          const response = await quoteService.delete(orcamento.id)
          if (response.success) {
            showToast('Orçamento deletado com sucesso!', 'success')
            loadOrcamentos()
          } else {
            showToast(response.message || 'Erro ao deletar orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao deletar orçamento:', error)
          showToast(error.response?.data?.message || 'Erro ao deletar orçamento', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Rascunho', className: 'status-draft', icon: FileText },
      open: { label: 'Aberto', className: 'status-open', icon: Clock },
      approved: { label: 'Aprovado', className: 'status-approved', icon: CheckCircle },
      expired: { label: 'Vencido', className: 'status-expired', icon: AlertCircle },
      cancelled: { label: 'Cancelado', className: 'status-cancelled', icon: XCircle },
      converted: { label: 'Convertido', className: 'status-converted', icon: Package }
    }

    const config = statusConfig[status] || { label: status, className: 'status-default', icon: FileText }
    const Icon = config.icon

    return (
      <span className={`status-badge ${config.className}`}>
        <Icon size={14} />
        {config.label}
      </span>
    )
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  // Função para escapar HTML

  const handleGeneratePDF = async (orcamento) => {
    try {
      setGeneratingPDF(true)
      setEngineLogs('Carregando dados do orçamento...')
      
      // Carregar dados completos do orçamento
      const response = await quoteService.getById(orcamento.id)
      if (!response.success || !response.data) {
        showToast('Erro ao carregar dados do orçamento', 'error')
        setGeneratingPDF(false)
        return
      }

      const quote = response.data
      const items = quote.items || []

      // Carregar dados do cliente se houver customer_id
      let customer = null
      if (quote.customer_id) {
        try {
          const customerResponse = await customerService.getById(quote.customer_id)
          if (customerResponse.success && customerResponse.data) {
            customer = customerResponse.data
          }
        } catch (error) {
          console.error('Erro ao carregar cliente:', error)
        }
      }

      // Preparar dados do orçamento para o PDF
      const quoteData = {
        id: quote.id,
        number: quote.number || quote.id,
        customer: customer || {
          name: quote.customer_name || 'Não informado',
          cpf_cnpj: null,
          email: null,
          phone: null,
          cellphone: null
        },
        validityDate: quote.validity_date,
        paymentCondition: quote.payment_condition,
        customerObservation: quote.customer_observation,
        quoteDate: quote.quote_date || quote.created_at,
        items: items.map(item => ({
          product_name: item.product_name || 'Produto não informado',
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          total_price: item.total_price
        })),
        subtotal: parseFloat(quote.total_amount || 0),
        discount: parseFloat(quote.discount_amount || 0),
        total: parseFloat(quote.final_amount || quote.total_amount || 0),
        status: quote.status
      }

      setPdfQuoteData(quoteData)
      setEngineLogs('Preparando documento...')
      
      // Aguardar um pouco para garantir que os componentes foram renderizados
      await new Promise(r => setTimeout(r, 500))
      
      if (!pdfItemsRef.current || !pdfSummaryRef.current) {
        showToast('Erro ao gerar PDF. Tente novamente.', 'error')
        setGeneratingPDF(false)
        return
      }

      setEngineLogs('Renderizando documento...')
      
      // Renderizar duas partes separadas: itens e resumo
      const [itemsCanvas, summaryCanvas] = await Promise.all([
        html2canvas(pdfItemsRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#FFFFFF",
          logging: false,
          windowWidth: 1200,
          allowTaint: false,
          removeContainer: false,
        }),
        html2canvas(pdfSummaryRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#FFFFFF",
          logging: false,
          windowWidth: 1200,
          allowTaint: false,
          removeContainer: false,
        })
      ])
      
      setEngineLogs('Gerando PDF...')
      await new Promise(r => setTimeout(r, 400))
      
      const itemsImgData = itemsCanvas.toDataURL('image/jpeg', 0.98)
      const summaryImgData = summaryCanvas.toDataURL('image/jpeg', 0.98)
      const pdf = new jsPDF('p', 'mm', 'a4')
      
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      
      // Margens do PDF (uniformes para evitar páginas em branco)
      const margin = 10
      const spacing = 5 // Espaçamento entre seções
      const contentWidth = pdfWidth - (margin * 2)
      
      // Calcular dimensões das imagens proporcionalmente
      const itemsImgHeight = (itemsCanvas.height * contentWidth) / itemsCanvas.width
      const summaryImgHeight = (summaryCanvas.height * contentWidth) / summaryCanvas.width
      
      // Altura disponível por página (mesma para todas as páginas)
      const availableHeightPerPage = pdfHeight - (margin * 2)
      
      setEngineLogs('Finalizando...')
      
      // === PARTE 1: Adicionar seção de itens ===
      let currentY = margin // Posição Y atual
      let itemsHeightRemaining = itemsImgHeight
      let itemsSourceY = 0 // Posição de origem na imagem dos itens
      
      // Primeira página: adicionar o máximo possível da seção de itens
      pdf.addImage(
        itemsImgData, 
        'JPEG', 
        margin, 
        currentY, 
        contentWidth, 
        itemsImgHeight, 
        undefined, 
        'FAST'
      )
      
      itemsHeightRemaining -= availableHeightPerPage
      itemsSourceY = availableHeightPerPage
      currentY = margin
      
      // Adicionar páginas adicionais se os itens não couberem em uma página
      while (itemsHeightRemaining > 0) {
        pdf.addPage()
        currentY = margin
        
        // Calcular deslocamento Y para mostrar a continuação correta da imagem
        const yOffset = -(itemsSourceY - margin)
        
        pdf.addImage(
          itemsImgData, 
          'JPEG', 
          margin, 
          currentY + yOffset, 
          contentWidth, 
          itemsImgHeight, 
          undefined, 
          'FAST'
        )
        
        itemsHeightRemaining -= availableHeightPerPage
        itemsSourceY += availableHeightPerPage
      }
      
      // Calcular quanto de altura foi usado na última página de itens
      const lastPageItemsHeight = itemsImgHeight % availableHeightPerPage || 
                                  (itemsImgHeight <= availableHeightPerPage ? itemsImgHeight : availableHeightPerPage)
      
      // === PARTE 2: Adicionar seção de resumo ===
      // Calcular espaço disponível na página atual (última página de itens)
      const spaceLeftInCurrentPage = availableHeightPerPage - lastPageItemsHeight
      
      // Verificar se o resumo cabe na página atual com espaçamento adequado
      const summaryWithSpacing = summaryImgHeight + spacing
      const needsNewPage = summaryWithSpacing > spaceLeftInCurrentPage
      
      if (needsNewPage) {
        // Criar nova página para o resumo
        pdf.addPage()
        currentY = margin
        
        // Verificar se o resumo cabe em uma página
        if (summaryImgHeight <= availableHeightPerPage) {
          // Cabe em uma página - adicionar normalmente
          pdf.addImage(
            summaryImgData, 
            'JPEG', 
            margin, 
            currentY, 
            contentWidth, 
            summaryImgHeight, 
            undefined, 
            'FAST'
          )
        } else {
          // Resumo precisa de múltiplas páginas (caso raro)
          let summaryHeightRemaining = summaryImgHeight
          let summarySourceY = 0
          
          while (summaryHeightRemaining > 0) {
            if (summarySourceY > 0) {
              pdf.addPage()
              currentY = margin
            }
            
            const yOffset = -(summarySourceY - margin)
            pdf.addImage(
              summaryImgData, 
              'JPEG', 
              margin, 
              currentY + yOffset, 
              contentWidth, 
              summaryImgHeight, 
              undefined, 
              'FAST'
            )
            
            summaryHeightRemaining -= availableHeightPerPage
            summarySourceY += availableHeightPerPage
          }
        }
      } else {
        // Adicionar resumo na mesma página, abaixo dos itens
        currentY = margin + lastPageItemsHeight + spacing
        pdf.addImage(
          summaryImgData, 
          'JPEG', 
          margin, 
          currentY, 
          contentWidth, 
          summaryImgHeight, 
          undefined, 
          'FAST'
        )
      }
      
      setEngineLogs('Concluído!')
      await new Promise(r => setTimeout(r, 400))
      
      const fileName = `Orcamento_${quoteData.number || quoteData.id}_${new Date().getFullYear()}_${Date.now()}.pdf`
      pdf.save(fileName)
      
      showToast('PDF gerado com sucesso!', 'success')
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      showToast('Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setPdfQuoteData(null)
    }
  }

  const columns = [
    {
      key: 'number',
      label: 'Número',
      render: (value, item) => {
        if (!item) return '-'
        return <span className="quote-number">#{item.number || value}</span>
      }
    },
    {
      key: 'customer_name',
      label: 'Cliente',
      render: (value, item) => {
        if (!item) return '-'
        return item.customer_name || value || '-'
      }
    },
    {
      key: 'quote_date',
      label: 'Data',
      render: (value, item) => {
        if (!item) return '-'
        return formatDate(item.quote_date || value)
      }
    },
    {
      key: 'validity_date',
      label: 'Validade',
      render: (value, item) => {
        if (!item || !item.validity_date) return '-'
        const isValid = item.validity_date && new Date(item.validity_date) >= new Date()
        return (
          <span className={!isValid ? 'expired' : ''}>
            {formatDate(item.validity_date)}
          </span>
        )
      }
    },
    {
      key: 'final_amount',
      label: 'Valor Total',
      render: (value, item) => {
        if (!item) return '-'
        return formatCurrency(item.final_amount || value || 0)
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, item) => {
        if (!item) return '-'
        return getStatusBadge(item.status || value || 'draft')
      }
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (value, item) => {
        if (!item) return null
        return (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <div className="menu-wrapper" ref={menuRef}>
              <button
                className={`menu-trigger ${activeMenu === item.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveMenu(activeMenu === item.id ? null : item.id)
                }}
              >
                <MoreVertical size={20} />
              </button>

              {activeMenu === item.id && (
                <div 
                  className="dropdown-menu"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setActiveMenu(null)
                      navigate(`/erp/orcamentos/${item.id}`)
                    }}
                  >
                    <Eye size={14} />
                    Visualizar
                  </button>

                  {hasPermission(MODULES.QUOTES, ACTIONS.CREATE) && (
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setActiveMenu(null)
                        navigate(`/erp/orcamentos/novo?duplicar=${item.id}`)
                      }}
                    >
                      <Copy size={14} />
                      Duplicar
                    </button>
                  )}
                  
                  {item.status === 'draft' && hasPermission(MODULES.QUOTES, ACTIONS.UPDATE) && (
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setActiveMenu(null)
                        navigate(`/erp/orcamentos/${item.id}/editar`)
                      }}
                    >
                      <Edit size={14} />
                      Editar
                    </button>
                  )}
                  
                  {(item.status === 'draft' || item.status === 'open') && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_APPROVE) && (
                    <button
                      className="dropdown-item success"
                      onClick={() => {
                        setActiveMenu(null)
                        handleApprove(item)
                      }}
                    >
                      <CheckCircle size={14} />
                      Aprovar
                    </button>
                  )}

                  {item.status === 'approved' && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_REVERT_TO_DRAFT) && (
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setActiveMenu(null)
                        handleRevertToDraft(item)
                      }}
                    >
                      <RotateCcw size={14} />
                      Voltar para Rascunho
                    </button>
                  )}
                  
                  {(item.status === 'open' || item.status === 'approved' || item.status === 'draft') && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_CANCEL) && (
                    <>
                      <div className="dropdown-divider" />
                      <button
                        className="dropdown-item danger"
                        onClick={() => {
                          setActiveMenu(null)
                          handleCancel(item)
                        }}
                      >
                        <XCircle size={14} />
                        Cancelar
                      </button>
                    </>
                  )}
                  
                  <button
                    className="dropdown-item info"
                    onClick={() => {
                      setActiveMenu(null)
                      handleGeneratePDF(item)
                    }}
                  >
                    <Download size={14} />
                    Baixar PDF
                  </button>
                  
                  {item.status === 'draft' && hasPermission(MODULES.QUOTES, ACTIONS.DELETE) && (
                    <>
                      <div className="dropdown-divider" />
                      <button
                        className="dropdown-item danger"
                        onClick={() => {
                          setActiveMenu(null)
                          handleDelete(item)
                        }}
                      >
                        <Trash2 size={14} />
                        Deletar
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }
    }
  ]

  return (
    <div className="orcamentos-page">
      <div className="page-header">
        <div>
          <h1>Orçamentos</h1>
          <p>Gerencie seus orçamentos</p>
        </div>
        {hasPermission(MODULES.QUOTES, ACTIONS.CREATE) && (
          <button
            className="btn-primary"
            onClick={() => navigate('/erp/orcamentos/novo')}
          >
            <Plus size={18} />
            Novo Orçamento
          </button>
        )}
      </div>

      <div className="orcamentos-table-container">
        <div className="orcamentos-table-header">
          <div className="orcamentos-search-wrapper">
            <Search className="orcamentos-search-icon" size={18} />
            <input
              type="text"
              placeholder="Pesquisar orçamentos..."
              className="orcamentos-search-input"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </div>

        <div className="orcamentos-table-wrapper">
          <DataTable
            data={orcamentos}
            columns={columns}
            loading={loading}
            emptyMessage="Nenhum orçamento encontrado"
            pagination={pagination.totalPages > 0 ? {
              from: ((pagination.page - 1) * pagination.limit) + 1,
              to: Math.min(pagination.page * pagination.limit, pagination.total),
              total: pagination.total,
              current: pagination.page,
              totalPages: pagination.totalPages,
              hasPrev: pagination.page > 1,
              hasNext: pagination.page < pagination.totalPages,
              onPrev: () => setPagination(prev => ({ ...prev, page: prev.page - 1 })),
              onNext: () => setPagination(prev => ({ ...prev, page: prev.page + 1 }))
            } : null}
          />
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      {/* Overlay de Geração de PDF */}
      {generatingPDF && (
        <div className="follow-up-pdf-overlay">
          <div className="follow-up-pdf-overlay-content">
            <div className="follow-up-pdf-icon-wrapper">
              <Cpu size={32} className="spinner" />
              <div className="follow-up-pdf-scan" />
            </div>
            <div>
              <h4 className="follow-up-pdf-title">Gerando PDF</h4>
              <p className="follow-up-pdf-subtitle">Exportando Orçamento</p>
            </div>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="follow-up-pdf-log">
                <p className="follow-up-pdf-log-text">{engineLogs}</p>
              </div>
              <div className="follow-up-pdf-progress-bar">
                <div className="follow-up-pdf-progress-fill" />
              </div>
            </div>
          </div>
        </div>
      )}

      {pdfQuoteData && (
        <QuotePDFTemplate
          pdfQuoteData={pdfQuoteData}
          itemsRef={pdfItemsRef}
          summaryRef={pdfSummaryRef}
        />
      )}
    </div>
  )
}

export default OrcamentosPage

